import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";

// Clave de localStorage para no ser pesados: si el usuario cierra el
// aviso, no se le vuelve a mostrar durante este número de días.
const DISMISS_KEY = "aria_install_dismissed_at";
const DISMISS_DAYS = 14;

function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent || "");
}

function isStandalone() {
  // True si la app ya se está ejecutando instalada (PWA / TWA), tanto en
  // Android (display-mode: standalone) como en iOS (navigator.standalone).
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function wasRecentlyDismissed() {
  const dismissedAt = localStorage.getItem(DISMISS_KEY);
  if (!dismissedAt) return false;
  const days = (Date.now() - Number(dismissedAt)) / (1000 * 60 * 60 * 24);
  return days < DISMISS_DAYS;
}

export default function InstallPrompt() {
  const { lang } = useAuth() || {};
  const isEn = lang === "en";

  const [deferredEvent, setDeferredEvent] = useState(null);
  const [platform, setPlatform] = useState(null); // "android" | "ios"
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone() || wasRecentlyDismissed()) return;

    if (isIos()) {
      // iOS no dispara ningún evento — mostramos instrucciones manuales
      // tras una pequeña pausa, para no ser intrusivos al entrar.
      setPlatform("ios");
      const timer = setTimeout(() => setVisible(true), 2500);
      return () => clearTimeout(timer);
    }

    // Android / Chrome / Edge: Chrome decide si dispara este evento según
    // sus propias heurísticas de "engagement" (manifest válido, https,
    // service worker activo, alguna interacción previa con el sitio).
    function handler(e) {
      e.preventDefault();
      setDeferredEvent(e);
      setPlatform("android");
      setVisible(true);
    }
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  }

  async function handleInstallClick() {
    if (!deferredEvent) return;
    deferredEvent.prompt();
    await deferredEvent.userChoice; // { outcome: "accepted" | "dismissed" }
    dismiss();
  }

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label={isEn ? "Install app" : "Instalar aplicación"}
      style={{
        position: "fixed",
        bottom: "calc(16px + env(safe-area-inset-bottom))",
        left: 16,
        right: 16,
        maxWidth: 420,
        margin: "0 auto",
        zIndex: 1000,
        background: "#000",
        color: "#fff",
        border: "1px solid #D4AF37",
        borderRadius: 14,
        padding: "14px 14px",
        boxShadow: "0 10px 30px rgba(0,0,0,.35)",
        display: "flex",
        alignItems: "center",
        gap: 12,
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          flexShrink: 0,
          background: "#000",
          border: "1px solid #D4AF37",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <img src="/icon-192.png" alt="" style={{ width: 28, height: 28 }} />
      </div>

      <div style={{ flex: 1, fontSize: 13, lineHeight: 1.35 }}>
        <strong style={{ display: "block", marginBottom: 2 }}>ARIA Wallet</strong>
        {platform === "ios" ? (
          <span>
            {isEn
              ? <>Tap <strong>Share ⬆️</strong> then <strong>"Add to Home Screen"</strong></>
              : <>Toca <strong>Compartir ⬆️</strong> y luego <strong>"Añadir a pantalla de inicio"</strong></>}
          </span>
        ) : (
          <span>{isEn ? "Install the app for faster access" : "Instala la app para un acceso más rápido"}</span>
        )}
      </div>

      {platform === "android" && (
        <button
          onClick={handleInstallClick}
          style={{
            background: "#D4AF37",
            color: "#000",
            border: "none",
            borderRadius: 8,
            padding: "8px 14px",
            fontWeight: 700,
            fontSize: 13,
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          {isEn ? "Install" : "Instalar"}
        </button>
      )}

      <button
        onClick={dismiss}
        aria-label={isEn ? "Close" : "Cerrar"}
        style={{
          background: "none",
          border: "none",
          color: "#999",
          fontSize: 18,
          lineHeight: 1,
          cursor: "pointer",
          padding: 4,
          flexShrink: 0,
        }}
      >
        ×
      </button>
    </div>
  );
}
